/* ============================================================
   EMMERA — ANIMATIONS V2
   Bibliothèque complète d'animations par type de page
   prefers-reduced-motion respecté · RAF · IntersectionObserver
   ============================================================ */

(function () {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const IO_OPTS = { threshold: 0.15, rootMargin: '0px 0px -40px 0px' };

  /* ── Utilitaire IntersectionObserver ── */
  function observe(selector, cb, opts) {
    const els = document.querySelectorAll(selector);
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { cb(e.target); io.unobserve(e.target); } });
    }, opts || IO_OPTS);
    els.forEach((el) => io.observe(el));
  }

  /* ══════════════════════════════════════════════════════════
     1. TYPEWRITER — lettre par lettre avec cursor or
     Usage : <h1 data-emm="typewriter" data-speed="60" data-delay="300">Texte</h1>
  ══════════════════════════════════════════════════════════ */
  function initTypewriter() {
    document.querySelectorAll('[data-emm="typewriter"]').forEach((el) => {
      const text  = el.textContent.trim();
      const speed = parseInt(el.dataset.speed  || '60',  10);
      const delay = parseInt(el.dataset.delay  || '300', 10);
      el.textContent = '';
      el.classList.add('emm-tw');

      if (REDUCED) { el.textContent = text; el.classList.remove('emm-tw'); return; }

      setTimeout(async () => {
        el.classList.add('emm-tw--writing');
        for (const char of text) {
          el.textContent += char;
          await new Promise((r) => setTimeout(r, speed));
        }
        setTimeout(() => el.classList.remove('emm-tw--writing'), 600);
      }, delay);
    });
  }

  /* ══════════════════════════════════════════════════════════
     2. WORD REVEAL — chaque mot fade-in + translateY
     Usage : <p data-emm="word-reveal">Votre texte ici</p>
  ══════════════════════════════════════════════════════════ */
  function initWordReveal() {
    document.querySelectorAll('[data-emm="word-reveal"]').forEach((el) => {
      if (REDUCED) return;
      const words = el.textContent.trim().split(/\s+/);
      el.innerHTML = words.map((w, i) =>
        `<span class="emm-word" style="--emm-wi:${i}">${w}</span>`
      ).join(' ');

      observe(null, null); // handled below via direct IO
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.querySelectorAll('.emm-word').forEach((w) => w.classList.add('emm-word--visible'));
            io.unobserve(e.target);
          }
        });
      }, IO_OPTS);
      io.observe(el);
    });
  }

  /* ══════════════════════════════════════════════════════════
     3. COUNTER ANIMATE — 0 → valeur easeOut 2s
     Usage : <span data-emm="counter" data-target="12000" data-suffix="+"></span>
  ══════════════════════════════════════════════════════════ */
  function initCounters() {
    observe('[data-emm="counter"]', (el) => {
      const target   = parseFloat(el.dataset.target || '0');
      const suffix   = el.dataset.suffix || '';
      const prefix   = el.dataset.prefix || '';
      const decimals = el.dataset.decimals ? parseInt(el.dataset.decimals, 10) : 0;
      const dur      = 2000;
      if (REDUCED) { el.textContent = prefix + target.toLocaleString('fr-FR') + suffix; return; }
      const start = performance.now();
      const tick = (now) => {
        const p    = Math.min((now - start) / dur, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        const val  = ease * target;
        el.textContent = prefix + (decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString('fr-FR')) + suffix;
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = prefix + target.toLocaleString('fr-FR') + suffix;
      };
      requestAnimationFrame(tick);
    });
  }

  /* ══════════════════════════════════════════════════════════
     4. PARALLAX SCROLL — translateY factor 0.25 desktop only
     Usage : <div data-emm="parallax" data-factor="0.25">...</div>
  ══════════════════════════════════════════════════════════ */
  function initParallax() {
    if (REDUCED || window.innerWidth < 768) return;
    const els = [...document.querySelectorAll('[data-emm="parallax"]')];
    if (!els.length) return;

    const tick = () => {
      els.forEach((el) => {
        const rect   = el.getBoundingClientRect();
        const factor = parseFloat(el.dataset.factor || '0.25');
        const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * factor;
        el.style.transform = `translateY(${offset}px)`;
      });
    };

    window.addEventListener('scroll', tick, { passive: true });
    tick();
  }

  /* ══════════════════════════════════════════════════════════
     5. STAGGER CARDS — cascade fade-in + scale
     Usage : <div data-emm="stagger-parent"><div>carte</div>...</div>
  ══════════════════════════════════════════════════════════ */
  function initStaggerCards() {
    document.querySelectorAll('[data-emm="stagger-parent"]').forEach((parent) => {
      if (REDUCED) return;
      const children = [...parent.children];
      children.forEach((c, i) => {
        c.classList.add('emm-stagger-card');
        c.style.setProperty('--emm-si', i);
      });

      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.querySelectorAll('.emm-stagger-card').forEach((c) => c.classList.add('emm-stagger-card--visible'));
            io.unobserve(e.target);
          }
        });
      }, IO_OPTS);
      io.observe(parent);
    });
  }

  /* ══════════════════════════════════════════════════════════
     6. LINE DRAW — SVG strokeDashoffset
     Usage : <svg data-emm="line-draw"><path .../></svg>
  ══════════════════════════════════════════════════════════ */
  function initLineDraws() {
    observe('[data-emm="line-draw"]', (svg) => {
      if (REDUCED) return;
      svg.querySelectorAll('path, line, polyline').forEach((p) => {
        const len = p.getTotalLength ? p.getTotalLength() : 200;
        p.style.strokeDasharray  = len;
        p.style.strokeDashoffset = len;
        p.style.transition = 'stroke-dashoffset 1.2s cubic-bezier(0.16, 1, 0.3, 1)';
        requestAnimationFrame(() => { p.style.strokeDashoffset = '0'; });
      });
    });
  }

  /* ══════════════════════════════════════════════════════════
     7. SPLIT REVEAL — texte depuis gauche, image depuis droite
     Usage : <div data-emm="split-reveal">
               <div data-split="left">texte</div>
               <div data-split="right">image</div>
             </div>
  ══════════════════════════════════════════════════════════ */
  function initSplitReveal() {
    document.querySelectorAll('[data-emm="split-reveal"]').forEach((wrap) => {
      if (REDUCED) return;
      const left  = wrap.querySelector('[data-split="left"]');
      const right = wrap.querySelector('[data-split="right"]');
      if (left)  left.classList.add('emm-split--left');
      if (right) right.classList.add('emm-split--right');

      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            if (left)  left.classList.add('emm-split--visible');
            if (right) right.classList.add('emm-split--visible');
            io.unobserve(e.target);
          }
        });
      }, IO_OPTS);
      io.observe(wrap);
    });
  }

  /* ══════════════════════════════════════════════════════════
     8. MARQUEE PAUSE — ralentit au hover
     Usage : <div data-emm="marquee-pause">...</div>
  ══════════════════════════════════════════════════════════ */
  function initMarqueePause() {
    document.querySelectorAll('[data-emm="marquee-pause"]').forEach((el) => {
      el.addEventListener('mouseenter', () => el.style.setProperty('--emm-marquee-speed', '80s'));
      el.addEventListener('mouseleave', () => el.style.removeProperty('--emm-marquee-speed'));
    });
  }

  /* ══════════════════════════════════════════════════════════
     9. MORPH PHASE — lunes SVG morphent entre 4 phases
     Usage : <div data-emm="morph-phase" data-phase="menstruelle">...</div>
  ══════════════════════════════════════════════════════════ */
  const PHASE_COLORS = {
    menstruelle:  '#8B3A52',
    folliculaire: '#E6C6C6',
    ovulatoire:   '#C8AE7D',
    luteale:      '#9AA89B',
  };
  function initMorphPhase() {
    document.querySelectorAll('[data-emm="morph-phase"]').forEach((el) => {
      const phases  = Object.keys(PHASE_COLORS);
      let   current = phases.indexOf(el.dataset.phase || 'menstruelle');
      const svg     = el.querySelector('svg');
      if (!svg || REDUCED) return;

      el.addEventListener('mouseenter', () => {
        current = (current + 1) % phases.length;
        const color = PHASE_COLORS[phases[current]];
        svg.querySelectorAll('circle, path').forEach((s) => {
          s.style.transition = 'fill 0.6s ease, stroke 0.6s ease';
          if (s.getAttribute('fill') !== 'none') s.style.fill   = color;
          if (s.getAttribute('stroke'))          s.style.stroke = color;
        });
      });
    });
  }

  /* ══════════════════════════════════════════════════════════
     10. FLOAT — flottement doux infini
     Usage : <div data-emm="float">...</div>
  ══════════════════════════════════════════════════════════ */
  function initFloat() {
    if (REDUCED) return;
    document.querySelectorAll('[data-emm="float"]').forEach((el, i) => {
      el.style.animation = `emm-float 3s ease-in-out ${i * 0.4}s infinite alternate`;
    });
  }

  /* ══════════════════════════════════════════════════════════
     FADE UP GÉNÉRAL — data-anim="fade-up"
     Déjà géré par emmera-animations.js mais on s'assure
     qu'il est bien initialisé ici aussi
  ══════════════════════════════════════════════════════════ */
  function initFadeUp() {
    observe('[data-emm="fade-up"]', (el) => {
      el.classList.add('emm-fade-up--visible');
    });
    observe('[data-emm="fade-in"]', (el) => {
      el.classList.add('emm-fade-in--visible');
    });
  }

  /* ══════════════════════════════════════════════════════════
     CSS INJECTÉE — toutes les classes animation
  ══════════════════════════════════════════════════════════ */
  const CSS = `
/* ── Typewriter ── */
.emm-tw { display: inline-block; }
.emm-tw--writing::after {
  content: '|';
  display: inline-block;
  color: #C8AE7D;
  animation: emm-blink 0.7s step-end infinite;
  margin-left: 2px;
}
@keyframes emm-blink { 0%,100%{opacity:1} 50%{opacity:0} }

/* ── Word Reveal ── */
.emm-word {
  display: inline-block;
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 0.55s cubic-bezier(0.22,1,0.36,1), transform 0.55s cubic-bezier(0.22,1,0.36,1);
  transition-delay: calc(var(--emm-wi) * 80ms);
}
.emm-word--visible { opacity: 1; transform: translateY(0); }

/* ── Stagger Cards ── */
.emm-stagger-card {
  opacity: 0;
  transform: scale(0.96) translateY(20px);
  transition: opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1);
  transition-delay: calc(var(--emm-si) * 120ms);
}
.emm-stagger-card--visible { opacity: 1; transform: scale(1) translateY(0); }

/* ── Split Reveal ── */
.emm-split--left  { opacity: 0; transform: translateX(-40px); transition: opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1); }
.emm-split--right { opacity: 0; transform: translateX(40px);  transition: opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1); transition-delay: 120ms; }
.emm-split--visible { opacity: 1; transform: translateX(0); }

/* ── Fade Up ── */
[data-emm="fade-up"]  { opacity: 0; transform: translateY(28px); transition: opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1); }
[data-emm="fade-in"]  { opacity: 0; transition: opacity 0.7s ease; }
.emm-fade-up--visible  { opacity: 1 !important; transform: translateY(0) !important; }
.emm-fade-in--visible  { opacity: 1 !important; }

/* ── Float ── */
@keyframes emm-float { from{transform:translateY(0)} to{transform:translateY(-8px)} }

/* ── Reduced motion override ── */
@media (prefers-reduced-motion: reduce) {
  .emm-word, .emm-stagger-card, .emm-split--left, .emm-split--right,
  [data-emm="fade-up"], [data-emm="fade-in"] {
    opacity: 1 !important; transform: none !important; transition: none !important;
  }
}
`;

  /* ── Inject CSS ── */
  const style = document.createElement('style');
  style.id = 'emm-anim-v2';
  style.textContent = CSS;
  document.head.appendChild(style);

  /* ── Init all ── */
  function init() {
    initTypewriter();
    initWordReveal();
    initCounters();
    initParallax();
    initStaggerCards();
    initLineDraws();
    initSplitReveal();
    initMarqueePause();
    initMorphPhase();
    initFloat();
    initFadeUp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Exposer l'API globale ── */
  window.EmmeraAnimations = { init, initTypewriter, initWordReveal, initCounters, initParallax, initStaggerCards, initSplitReveal };

})();
